// The bet lifecycle, in one hook.
//
// A round trip through a cabinet is five steps and every one of them is a thing that can fail
// in a way the player deserves to be told about in English:
//   connect → approve (only if the allowance is short) → place → wait for the beacon → settle.
//
// There is no casino server anywhere in this. The bet is a transaction, the settlement is a
// transaction, and the number that decides both comes from drand.
//
// Practice mode (lib/casino.js PRACTICE) keeps the same five steps and removes the wallet and the
// chain: the money is a number in localStorage, the round is the next one the beacon publishes,
// and the settlement is the contract's own arithmetic run in this tab (lib/casino-math.js).
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ADDRESSES,
  CHIP,
  LIVE,
  PRACTICE,
  PRACTICE_MIN_ALL_IN,
  PRACTICE_START,
  allowanceFor,
  approve,
  chipBalance,
  fetchRound,
  askKeeper,
  houseState,
  maxAllIn,
  waitForSettled,
  KEEPER_URL,
  nextPracticeId,
  placeCall,
  practiceBalance,
  practiceHouse,
  roundAt,
  setPracticeBalance,
  settle,
  timeOfRound,
  uncompressG1,
} from 'lib/casino';
import { payoutFor, reservation, splitAllIn, ticketFor } from 'lib/casino-math';
import {
  connect as walletConnect,
  currentAccount,
  forgetWallet,
  formatUnits,
  hasWallet,
  humanError,
  parseUnits,
  requestWallets,
  sendTx,
  waitForReceipt,
} from 'lib/eth';
import { TOPIC } from 'lib/casino-abi';
import sfx from 'lib/sfx';

const ZERO = '0x0000000000000000000000000000000000000000';

/**
 * The result, in sound. The bands are multiples of the stake rather than absolute chips, so a
 * big win is big for the player who placed it: 20x and over is the jackpot, 4x and over is a big
 * win, any other payout is a small one, and nothing paid is a loss. A cabinet that hands over a
 * non-bigint gets the loss sound rather than silence — the player always hears the round close.
 */
function announceResult(payout, stake) {
  const paid = typeof payout === 'bigint' ? payout : BigInt(0);
  const risked = typeof stake === 'bigint' ? stake : BigInt(0);
  if (paid <= BigInt(0)) return sfx.lose();
  if (risked > BigInt(0) && paid >= risked * BigInt(20)) return sfx.jackpot();
  if (risked > BigInt(0) && paid >= risked * BigInt(4)) return sfx.win('big');
  return sfx.win('small');
}

/** Wallet, chip balance and house state, refreshed on a slow tick. */
export function useCasino() {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(PRACTICE ? practiceBalance() : null);
  const [allowance, setAllowance] = useState(null);
  const [house, setHouse] = useState(PRACTICE ? practiceHouse() : null);
  const [error, setError] = useState('');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(
    async who => {
      if (PRACTICE) {
        setBalance(practiceBalance());
        setHouse(practiceHouse());
        return;
      }
      const target = who === undefined ? account : who;
      if (!LIVE) return;
      try {
        const [bal, allow, h] = await Promise.all([
          target ? chipBalance(target) : Promise.resolve(null),
          target ? allowanceFor(target) : Promise.resolve(null),
          houseState(),
        ]);
        if (!alive.current) return;
        setBalance(bal);
        setAllowance(allow);
        setHouse(h);
      } catch (err) {
        if (alive.current) setError(humanError(err));
      }
    },
    [account],
  );

  useEffect(() => {
    let stop = false;
    if (PRACTICE) {
      // No wallet is ever touched in practice. The tick keeps the play stack in step across tabs.
      const t = setInterval(() => refresh(), 20000);
      return () => clearInterval(t);
    }
    currentAccount()
      .then(a => {
        if (stop || !a) return;
        setAccount(a);
        refresh(a);
      })
      .catch(() => {});
    refresh(null);
    const t = setInterval(() => refresh(), 20000);
    return () => {
      stop = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // With two or more wallets injected, EIP-6963 announcement order decides nothing useful, so
  // the player picks. The choice is remembered, so this only asks once per browser.
  const [choices, setChoices] = useState([]);

  const connect = useCallback(
    async id => {
      setError('');
      try {
        const a = await walletConnect(id);
        setChoices([]);
        setAccount(a);
        await refresh(a);
        return a;
      } catch (err) {
        if (err && err.code === 'CHOOSE_WALLET') {
          setChoices(requestWallets().map(w => ({ ...w.info })));
          return null;
        }
        setError(humanError(err));
        return null;
      }
    },
    [refresh],
  );

  const cancelChoice = useCallback(() => setChoices([]), []);

  /** Disconnect the remembered wallet so the next connect asks again. */
  const switchWallet = useCallback(() => {
    forgetWallet();
    setAccount(null);
    setChoices(requestWallets().map(w => ({ ...w.info })));
  }, []);

  /** Put the play stack back to 1,000 chips. Practice only; a no-op on the live floor. */
  const resetPractice = useCallback(() => {
    if (!PRACTICE) return;
    setPracticeBalance(PRACTICE_START);
    setBalance(PRACTICE_START);
    setError('');
  }, []);

  return {
    account,
    balance,
    allowance,
    house,
    error,
    setError,
    connect,
    choices,
    cancelChoice,
    switchWallet,
    refresh,
    resetPractice,
    hasWallet: hasWallet(),
    live: LIVE,
    practice: PRACTICE,
    // The floor takes bets, real or play money.
    open: LIVE || PRACTICE,
    // This player can press the button: practice needs nothing, the live floor needs a wallet.
    ready: PRACTICE || (LIVE && !!account),
  };
}

const hexWord = (data, i) => data.replace(/^0x/, '').slice(i * 64, i * 64 + 64);

function findLog(receipt, topic, address) {
  const logs = receipt.logs || [];
  for (let i = 0; i < logs.length; i += 1) {
    const l = logs[i];
    const sameAddress = !address || String(l.address).toLowerCase() === String(address).toLowerCase();
    if (sameAddress && l.topics && String(l.topics[0]).toLowerCase() === topic.toLowerCase()) return l;
  }
  return null;
}

/**
 * Drive one bet from placement to settlement.
 * `phase` is what the cabinet renders: idle → approving → placing → waiting → settling → done.
 */
export function useBet(cabinetKey, casino) {
  const [phase, setPhase] = useState('idle');
  const [bet, setBet] = useState(null); // { id, round, stake, fee }
  const [result, setResult] = useState(null); // { won, payout, randomness }
  const [note, setNote] = useState('');
  const [countdown, setCountdown] = useState(0);

  const reset = useCallback(() => {
    setPhase('idle');
    setBet(null);
    setResult(null);
    setNote('');
    setCountdown(0);
  }, []);

  // Tick the "waiting for the beacon" clock so the wait is legible rather than a dead spinner.
  useEffect(() => {
    if (phase !== 'waiting' || !bet) return undefined;
    const t = setInterval(() => {
      const due = timeOfRound(bet.round);
      setCountdown(Math.max(0, due - Math.floor(Date.now() / 1000)));
    }, 250);
    return () => clearInterval(t);
  }, [phase, bet]);

  // One tick a second while the beacon is pending, so the wait has a pulse instead of a dead
  // spinner. Bounded on purpose: a round that refuses to publish can keep this phase open for
  // two minutes, and a two-minute metronome is not a sound, it is a hostage situation.
  useEffect(() => {
    if (phase !== 'waiting') return undefined;
    let ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      if (ticks > 30) {
        clearInterval(t);
        return;
      }
      sfx.countdownTick();
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Practice: no wallet, no contract, no signature. Same steps, same arithmetic, play money.
  const playPractice = useCallback(
    async (allIn, args) => {
      setResult(null);
      casino.setError('');
      let taken = BigInt(0);
      try {
        // The door: the same refusals the cabinet makes, with the same error names.
        if (allIn < PRACTICE_MIN_ALL_IN) throw new Error('TooSmall()');
        const { payoutMultBps } = ticketFor(cabinetKey, args);
        const { stake, fee } = splitAllIn(allIn);
        if (reservation(stake, fee, payoutMultBps) > practiceHouse().maxPayout) throw new Error('Insufficient()');
        const before = practiceBalance();
        if (allIn > before) throw new Error('Not enough play chips. RESET the stack from the lobby bar.');

        // 1. Take the money and name a round that has not published yet.
        setPhase('placing');
        setNote('Placing the practice bet…');
        setPracticeBalance(before - allIn);
        sfx.chip();
        taken = allIn;
        casino.refresh();
        const id = nextPracticeId(cabinetKey);
        const round = roundAt(Math.floor(Date.now() / 1000) + 3);
        setBet({ id, round, stake, fee, tx: null });

        // 2. Wait for it, exactly as the real cabinet does.
        setPhase('waiting');
        setNote(`Waiting for drand round ${round}…`);
        const { randomness, source } = await practiceRandomness(round);
        sfx.countdownGo();

        // 3. Settle with the contract's arithmetic, in this tab.
        setPhase('settling');
        setNote('Settling in your browser…');
        const payout = payoutFor(cabinetKey, id, stake, args, randomness);
        if (payout > BigInt(0)) setPracticeBalance(practiceBalance() + payout);
        taken = BigInt(0);
        setResult({ won: payout > BigInt(0), payout, randomness, round, id, tx: null, practice: true, source });
        announceResult(payout, stake);
        setPhase('done');
        setNote('');
        casino.refresh();
      } catch (err) {
        // A practice bet that cannot settle refunds on the spot — there is no hour to wait for.
        if (taken > BigInt(0)) setPracticeBalance(practiceBalance() + taken);
        casino.setError(humanError(err));
        setPhase('idle');
        setNote('');
        casino.refresh();
      }
    },
    [cabinetKey, casino],
  );

  const play = useCallback(
    async (allIn, args) => {
      if (PRACTICE) return playPractice(allIn, args);
      const { account, refresh } = casino;
      if (!account) {
        casino.setError('Connect a wallet first.');
        return;
      }
      if (ADDRESSES[cabinetKey] === ZERO) {
        casino.setError('This cabinet is not deployed yet.');
        return;
      }

      setResult(null);
      casino.setError('');

      try {
        // 1. Approve, and only for what this bet actually costs. No infinite allowance by default.
        const allowance = await allowanceFor(account);
        if (allowance < allIn) {
          setPhase('approving');
          setNote(`Approving ${CHIP.symbol} for this bet…`);
          const hash = await approve(account, allIn);
          await waitForReceipt(hash);
        }

        // 2. Place.
        setPhase('placing');
        setNote('Placing the bet…');
        sfx.chip();
        const callSpec = placeCall(cabinetKey, [allIn].concat(args));
        const placeHash = await sendTx(account, callSpec.to, callSpec.fn, callSpec.args);
        const placeReceipt = await waitForReceipt(placeHash);
        if (String(placeReceipt.status) !== '0x1') throw new Error('The bet was rejected on chain.');

        const log = findLog(placeReceipt, TOPIC.Placed, callSpec.to);
        if (!log) throw new Error('Could not read the bet back from the chain.');
        const id = BigInt(log.topics[1]);
        const round = Number(BigInt('0x' + hexWord(log.data, 2)));
        const stake = BigInt('0x' + hexWord(log.data, 0));
        const fee = BigInt('0x' + hexWord(log.data, 1));
        setBet({ id, round, stake, fee, tx: placeHash });


        // 3. Wait for the named round. It did not exist when the money went in.
        setPhase('waiting');
        setNote(`Waiting for drand round ${round}…`);
        const signature = await waitForBeacon(round);
        sfx.countdownGo();

        // 4. Settle. Give the keeper a short head start — if it lands the transaction the
        //    player never signs again, and we read the result out of its log instead.
        setPhase('settling');
        setNote('Settling…');
        let settled = null;
        let settleHash = null;

        // settle() has no access control, so ANYONE can close this bet — the keeper, a bot,
        // another tab — and the payout still goes to the address stored on it. Closing an
        // already-closed bet reverts with NotOpen, which reaches the player as a bare
        // 'execution reverted' on a bet they actually won. So look before signing.
        // The keeper cannot even try until the round exists, so ask now rather than at placement.
        // It answers synchronously, so a true here means the bet is ALREADY closed on chain and we
        // only need long enough for the log to be queryable. A false means sign now: waiting on a
        // rate-limited keeper was costing every player 20 seconds for nothing.
        const keeperDidIt = await askKeeper(cabinetKey, id);
        const already = await waitForSettled(cabinetKey, id, keeperDidIt ? 5000 : 0);
        if (already) {
          settled = already;
          settleHash = already.transactionHash;
        }

        if (!settled) {
          // Three things may close this bet: this tab, the keeper, or the settler on a cron.
          // Whoever lands it, the payout goes to the address stored on the bet, so the only
          // question is which of them tells us first. Run our own settle AND a chain watcher
          // together and take whichever produces a result. Watching only on error was the bug:
          // if somebody else settled while our transaction sat unmined, we waited out the
          // receipt timeout and reported 'timed out' on a bet that had already resolved.
          const watcher = waitForSettled(cabinetKey, id, 120000);
          const ours = (async () => {
            const hash = await settle(account, cabinetKey, id, signature);
            settleHash = hash;
            const receipt = await waitForReceipt(hash);
            if (String(receipt.status) !== '0x1') throw new Error('Settlement failed.');
            return findLog(receipt, TOPIC.Settled, callSpec.to);
          })();
          settled = await firstResult([watcher, ours]);
          if (settled && settled.transactionHash) settleHash = settled.transactionHash;
        }
        if (!settled) throw new Error('Could not read the result back from the chain.');
        const won = BigInt('0x' + hexWord(settled.data, 0)) !== BigInt(0);
        const payout = BigInt('0x' + hexWord(settled.data, 1));
        const randomness = '0x' + hexWord(settled.data, 2);

        setResult({ won, payout, randomness, round, id, tx: settleHash });
        announceResult(payout, stake);
        setPhase('done');
        setNote('');
        refresh();
      } catch (err) {
        casino.setError(humanError(err));
        // A placed bet that failed to settle is not lost: it stays open and settleable, and it
        // is refundable in full after an hour if the round genuinely never publishes.
        setPhase(bet ? 'stuck' : 'idle');
        setNote('');
      }
    },
    [cabinetKey, casino, bet, playPractice],
  );

  /** Retry settlement for a bet that was placed but never closed. */
  const retrySettle = useCallback(async () => {
    if (!bet || !casino.account) return;
    try {
      setPhase('settling');
      // Same race as the main path: something else may have closed this bet already.
      let settled = await waitForSettled(cabinetKey, bet.id, 0);
      let hash = settled ? settled.transactionHash : null;
      if (!settled) {
        const signature = await waitForBeacon(bet.round);
        try {
          hash = await settle(casino.account, cabinetKey, bet.id, signature);
          const receipt = await waitForReceipt(hash);
          settled = findLog(receipt, TOPIC.Settled, ADDRESSES[cabinetKey]);
        } catch (raceErr) {
          settled = await waitForSettled(cabinetKey, bet.id, 10000);
          if (!settled) throw raceErr;
          hash = settled.transactionHash;
        }
      }
      if (settled) {
        setResult({
          won: BigInt('0x' + hexWord(settled.data, 0)) !== BigInt(0),
          payout: BigInt('0x' + hexWord(settled.data, 1)),
          randomness: '0x' + hexWord(settled.data, 2),
          round: bet.round,
          id: bet.id,
          tx: hash,
        });
        setPhase('done');
        announceResult(BigInt('0x' + hexWord(settled.data, 1)), bet.stake);
        casino.refresh();
      }
    } catch (err) {
      casino.setError(humanError(err));
      setPhase('stuck');
    }
  }, [bet, casino, cabinetKey]);

  return { phase, bet, result, note, countdown, play, reset, retrySettle };
}

/**
 * Practice: the beacon's own randomness for a round (sha256 of the signature, the value the
 * contract stores), or, if every mirror is unreachable for 20 s, 32 bytes from this browser.
 * The receipt says which one it was.
 */
async function practiceRandomness(round) {
  const limit = Date.now() + 20000;
  for (;;) {
    const due = timeOfRound(round) * 1000;
    if (Date.now() < due) {
      await new Promise(r => setTimeout(r, Math.min(1000, due - Date.now() + 100)));
      continue;
    }
    try {
      const r = await fetchRound(round);
      if (r && /^[0-9a-f]{64}$/i.test(r.randomness || '')) {
        return { randomness: '0x' + r.randomness.toLowerCase(), source: 'drand' };
      }
    } catch (e) {
      /* mirror hiccup — try again below */
    }
    if (Date.now() > limit) break;
    await new Promise(r => setTimeout(r, 400));
  }
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return { randomness: '0x' + hex, source: 'local' };
}

/** Poll drand until the named round publishes, then hand back the uncompressed signature. */
/**
 * Resolve with the first entry that yields a result. Rejects only once every one of them has
 * failed or come back empty, so a losing racer can never fail a bet another racer settled.
 */
function firstResult(promises) {
  return new Promise((resolve, reject) => {
    let pending = promises.length;
    let lastError = null;
    for (const p of promises) {
      Promise.resolve(p).then(
        value => {
          if (value) resolve(value);
          else if (--pending === 0) reject(lastError || new Error('Could not read the result back from the chain.'));
        },
        err => {
          lastError = err;
          if (--pending === 0) reject(lastError);
        },
      );
    }
  });
}

async function waitForBeacon(round, timeoutMs) {
  const limit = Date.now() + (timeoutMs || 120000);
  for (;;) {
    const due = timeOfRound(round) * 1000;
    if (Date.now() < due) {
      await new Promise(r => setTimeout(r, Math.min(1000, due - Date.now() + 100)));
      continue;
    }
    try {
      const r = await fetchRound(round);
      if (r && r.signature) return uncompressG1(r.signature);
    } catch (e) {
      /* mirror hiccup — try again below */
    }
    if (Date.now() > limit) {
      throw new Error(
        'drand has not published that round yet. Your bet is safe and still settleable — try again in a moment.',
      );
    }
    await new Promise(r => setTimeout(r, 400));
  }
}


/**
 * The largest all-in this cabinet will accept RIGHT NOW, read from the contract.
 *
 * The house refuses any bet whose worst-case payout would exceed maxPayout, and the refusal is a
 * revert. Without this the player types a number, signs twice, and gets "execution reverted" with
 * nothing telling them the house simply cannot cover that win. Every cabinet shipped with a
 * hardcoded 5 MSFT default, which on a 0.15 bankroll is refused at every multiplier.
 *
 * Also clamps a stake that is already over the line, because the cap moves on its own: it falls
 * when someone else opens a big bet and rises when the bankroll grows.
 */
export function useStakeCap(cabinetKey, multBps, casino, amount, setAmount) {
  const [cap, setCap] = useState(null);
  // maxPayout is the only house number the cap depends on, so re-read when it moves.
  const houseKey = casino.house && casino.house.maxPayout !== undefined ? String(casino.house.maxPayout) : "";

  useEffect(() => {
    let alive = true;
    if (!LIVE || !multBps) {
      setCap(null);
      return undefined;
    }
    maxAllIn(cabinetKey, BigInt(Math.round(multBps)))
      .then(v => {
        if (alive) setCap(v);
      })
      .catch(() => {
        if (alive) setCap(null); // a failed read must not block play; the chain still decides
      });
    return () => {
      alive = false;
    };
  }, [cabinetKey, multBps, houseKey]);

  useEffect(() => {
    if (cap === null || cap <= BigInt(0)) return;
    let current;
    try {
      current = parseUnits(amount || "0", CHIP.decimals);
    } catch (e) {
      return; // mid-typing garbage; leave the box alone
    }
    if (current > cap) setAmount(formatUnits(cap, CHIP.decimals, 6));
    // Only react to the cap moving. Clamping on every keystroke fights the player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap]);

  return cap;
}
